import { useEffect, useState } from 'react';
import type { RegistoSiadap } from '@/context/SiadapContext';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';

interface SiadapDeleteModalProps {
  registo: RegistoSiadap;
  onConfirm: () => void;
  onClose: () => void;
}

export default function SiadapDeleteModal({ registo, onConfirm, onClose }: SiadapDeleteModalProps) {
  const { funcionarios } = useFuncionarios();
  const func = funcionarios.find((f) => f.id === registo.funcionarioId);
  const [confirmed, setConfirmed] = useState(false);
  const { animating, handleClose } = useModalAnimation(onClose);

  useModalOverflow();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); };
  }, [handleClose]);

  const initials = (func?.nome ?? registo.funcionarioId)
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-sm overflow-hidden border border-zinc-100 flex flex-col ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between flex-shrink-0 bg-zinc-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-rose-100 flex-shrink-0">
              <i className="ri-delete-bin-line text-rose-600 text-lg" />
            </div>
            <div>
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-0.5">Eliminar</p>
              <h2 className="text-[15px] font-bold text-zinc-900 leading-tight">Registo SIADAP</h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Funcionário */}
          <div className="flex items-center gap-3 p-3.5 bg-zinc-50 rounded-xl border border-zinc-100">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-zinc-900 text-white text-[12px] font-bold flex-shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-zinc-800 truncate">{func?.nome ?? registo.funcionarioId}</p>
              <p className="text-[11px] text-zinc-400">Período: <strong className="text-zinc-600">{registo.periodoAvaliacao}</strong></p>
            </div>
          </div>

          {/* Aviso */}
          <div className="flex items-start gap-3 bg-rose-50 border border-rose-200 rounded-xl p-3.5">
            <div className="w-5 h-5 flex items-center justify-center text-rose-500 flex-shrink-0 mt-0.5">
              <i className="ri-error-warning-line text-sm" />
            </div>
            <div>
              <p className="text-[12px] font-bold text-rose-800 mb-0.5">Esta ação não pode ser desfeita</p>
              <p className="text-[11px] text-rose-700 leading-relaxed">
                O registo SIADAP do período <strong>{registo.periodoAvaliacao}</strong> será eliminado permanentemente. Os pontos associados serão removidos do cálculo de progressão.
              </p>
            </div>
          </div>

          {/* Confirmação */}
          <div
            onClick={() => setConfirmed((v) => !v)}
            className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all ${
              confirmed ? 'bg-rose-50 border-rose-300' : 'bg-zinc-50 border-zinc-200 hover:border-zinc-300'
            }`}
          >
            <div className={`w-5 h-5 flex items-center justify-center rounded border-2 flex-shrink-0 transition-all ${
              confirmed ? 'bg-rose-500 border-rose-500' : 'border-zinc-300 bg-white'
            }`}>
              {confirmed && <i className="ri-check-line text-white text-xs" />}
            </div>
            <p className={`text-[12px] font-medium ${confirmed ? 'text-rose-700' : 'text-zinc-600'}`}>
              Confirmo que pretendo eliminar este registo
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-2.5 flex-shrink-0 bg-zinc-50/40">
          <button
            onClick={handleClose}
            className="whitespace-nowrap px-4 py-2 text-[13px] font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={!confirmed}
            className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-[13px] font-medium bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-delete-bin-line text-sm" />
            Eliminar Registo
          </button>
        </div>
      </div>
    </div>
  );
}
